import {
  signInWithPopup,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  FacebookAuthProvider,
  TwitterAuthProvider,
  OAuthProvider,
  User,
  UserCredential,
  AuthError,
  updateProfile,
  sendPasswordResetEmail,
  updatePassword,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { auth } from './firebaseService';
import { trackLogin } from './analyticsService';

// 사용자 정보 타입 정의
export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  createdAt: string;
  lastLoginAt: string;
}

// 인증 에러 타입
export interface AuthErrorInfo {
  code: string;
  message: string;
  details?: string;
}

// 구글 로그인
export const signInWithGoogle = async (): Promise<UserCredential> => {
  try {
    const provider = new GoogleAuthProvider();
    // 추가 스코프 요청 (선택사항)
    provider.addScope('email');
    provider.addScope('profile');
    
    const result = await signInWithPopup(auth, provider);
    trackLogin('google');
    return result;
  } catch (error) {
    console.error('❌ 구글 로그인 실패:', error);
    throw handleAuthError(error as AuthError);
  }
};

// 애플 로그인 (Firebase 표준 OAuth 팝업)
// 논스 생성·검증, redirectURI 처리를 모두 Firebase SDK가 담당한다.
// Apple Developer 콘솔의 Service ID(com.jalam.react.login)에
// Return URL "https://jalam-50cbd.firebaseapp.com/__/auth/handler" 등록 필요.
export const signInWithApple = async (): Promise<UserCredential> => {
  try {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    provider.setCustomParameters({ locale: 'ko' });

    const result = await signInWithPopup(auth, provider);
    trackLogin('apple');
    return result;
  } catch (error) {
    console.error('❌ 애플 로그인 실패:', error);
    throw handleAuthError(error as AuthError);
  }
};

// Apple 로그인 리다이렉트 결과 처리
export const handleAppleRedirectResult = async (): Promise<UserCredential | null> => {
  try {
    const result = await getRedirectResult(auth);
    return result;
  } catch (error) {
    console.error('❌ 애플 로그인 리다이렉트 결과 처리 실패:', error);
    throw handleAuthError(error as AuthError);
  }
};

// 페이스북 로그인
export const signInWithFacebook = async (): Promise<UserCredential> => {
  try {
    const provider = new FacebookAuthProvider();
    provider.addScope('email');
    
    const result = await signInWithPopup(auth, provider);
    return result;
  } catch (error) {
    console.error('❌ 페이스북 로그인 실패:', error);
    throw handleAuthError(error as AuthError);
  }
};

// 트위터 로그인
export const signInWithTwitter = async (): Promise<UserCredential> => {
  try {
    const provider = new TwitterAuthProvider();
    
    const result = await signInWithPopup(auth, provider);
    return result;
  } catch (error) {
    console.error('❌ 트위터 로그인 실패:', error);
    throw handleAuthError(error as AuthError);
  }
};

// nonce 생성 함수
const generateNonce = (): string => {
  const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._';
  let result = '';
  for (let i = 0; i < 32; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return result;
};



// 이메일/비밀번호 로그인
export const signInWithEmail = async (email: string, password: string): Promise<UserCredential> => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result;
  } catch (error) {
    console.error('❌ 이메일 로그인 실패:', error);
    throw handleAuthError(error as AuthError);
  }
};

// 이메일/비밀번호 회원가입
export const signUpWithEmail = async (
  email: string, 
  password: string, 
  displayName?: string
): Promise<UserCredential> => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    
    // 프로필 업데이트 (선택사항)
    if (displayName) {
      await updateProfile(result.user, { displayName });
    }
    
    return result;
  } catch (error) {
    console.error('❌ 회원가입 실패:', error);
    throw handleAuthError(error as AuthError);
  }
};

// 로그아웃
export const signOutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('❌ 로그아웃 실패:', error);
    throw handleAuthError(error as AuthError);
  }
};

// 인증 상태 변경 감지
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// 현재 사용자 정보 가져오기
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

// 사용자 프로필 업데이트
export const updateUserProfile = async (updates: {
  displayName?: string;
  photoURL?: string;
}): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('로그인된 사용자가 없습니다.');
    }
    
    await updateProfile(user, updates);
  } catch (error) {
    console.error('❌ 프로필 업데이트 실패:', error);
    throw handleAuthError(error as AuthError);
  }
};

// 비밀번호 재설정 이메일 발송
export const sendPasswordReset = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('❌ 비밀번호 재설정 이메일 발송 실패:', error);
    throw handleAuthError(error as AuthError);
  }
};

// 비밀번호 변경
export const changePassword = async (
  currentPassword: string, 
  newPassword: string
): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user || !user.email) {
      throw new Error('로그인된 사용자가 없습니다.');
    }
    
    // 현재 비밀번호로 재인증
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    
    // 새 비밀번호로 업데이트
    await updatePassword(user, newPassword);
  } catch (error) {
    console.error('❌ 비밀번호 변경 실패:', error);
    throw handleAuthError(error as AuthError);
  }
};

// 계정 삭제
export const deleteUserAccount = async (currentPassword: string): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user || !user.email) {
      throw new Error('로그인된 사용자가 없습니다.');
    }
    
    // 현재 비밀번호로 재인증
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    
    // 계정 삭제
    await deleteUser(user);
  } catch (error) {
    console.error('❌ 계정 삭제 실패:', error);
    throw handleAuthError(error as AuthError);
  }
};

// 사용자 프로필 정보를 커스텀 타입으로 변환
export const getUserProfile = (user: User): UserProfile => {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
    createdAt: user.metadata.creationTime || '',
    lastLoginAt: user.metadata.lastSignInTime || ''
  };
};

// 인증 에러 처리
export const handleAuthError = (error: AuthError): AuthErrorInfo => {
  let message = '알 수 없는 오류가 발생했습니다.';
  let details = '';
  
  switch (error.code) {
    case 'auth/user-not-found':
      message = '등록되지 않은 이메일입니다.';
      break;
    case 'auth/wrong-password':
      message = '잘못된 비밀번호입니다.';
      break;
    case 'auth/email-already-in-use':
      message = '이미 사용 중인 이메일입니다.';
      break;
    case 'auth/weak-password':
      message = '비밀번호가 너무 약합니다.';
      details = '비밀번호는 최소 6자 이상이어야 합니다.';
      break;
    case 'auth/invalid-email':
      message = '유효하지 않은 이메일 형식입니다.';
      break;
    case 'auth/too-many-requests':
      message = '너무 많은 요청이 발생했습니다.';
      details = '잠시 후 다시 시도해주세요.';
      break;
    case 'auth/network-request-failed':
      message = '네트워크 연결을 확인해주세요.';
      break;
    case 'auth/popup-closed-by-user':
      message = '로그인 창이 사용자에 의해 닫혔습니다.';
      break;
    case 'auth/cancelled-popup-request':
      message = '로그인 요청이 취소되었습니다.';
      break;
    case 'auth/operation-not-allowed':
      message = 'Apple 로그인이 활성화되지 않았습니다.';
      details = 'Firebase 콘솔에서 Authentication > Sign-in method에서 Apple을 활성화해주세요.';
      break;
    case 'auth/invalid-credential':
      // Apple Service ID 불일치 오류
      if (error.message && error.message.includes('audience')) {
        message = 'Apple Service ID가 일치하지 않습니다.';
        details = 'Firebase 콘솔 > Authentication > Sign-in method > Apple에서 Service ID를 "com.jalam.react.login"으로 설정해주세요.';
      } else {
        message = '유효하지 않은 인증 정보입니다.';
      }
      break;
    default:
      message = error.message || message;
  }
  
  return {
    code: error.code,
    message,
    details
  };
};

// 인증 상태 확인
export const isAuthenticated = (): boolean => {
  return !!getCurrentUser();
};

// 이메일 인증 상태 확인
export const isEmailVerified = (): boolean => {
  const user = getCurrentUser();
  return user ? user.emailVerified : false;
};
